/*
 * Bed builder data model. Turns raw Shopify products into styles -> colors -> pieces.
 * Pure functions only (no DOM) so it can be tested in Node against the live catalog.
 *
 * Piece types: duvet, sham, pillowcase, sheetset, bedskirt.
 * Bed sizes: twin, full, queen, king. Duvets come in twin / full-queen / king.
 */
(function (root) {
  var BED_SIZES = ['twin', 'full', 'queen', 'king'];

  function norm(s) {
    return String(s || '').toUpperCase().replace(/[“”"]/g, '').replace(/\s+/g, ' ').trim();
  }

  function sizeOf(v) {
    if (/FULL ?\/ ?QUEEN|FULL QUEEN/.test(v)) return 'fullqueen';
    if (/\bTWIN\b/.test(v)) return 'twin';
    if (/\bKING\b/.test(v)) return 'king';
    if (/\bQUEEN\b/.test(v)) return 'queen';
    if (/\bFULL\b/.test(v)) return 'full';
    return null;
  }

  function subtypeOf(v) {
    if (/GRAND EURO/.test(v)) return 'grand-euro';
    if (/EURO/.test(v)) return 'euro';
    if (/\bKING\b/.test(v)) return 'king';
    if (/STANDARD|\bSTD\b/.test(v)) return 'standard';
    return 'standard';
  }

  // Classify one variant's option value into a bed piece, using the product title as context.
  function classify(value, title, type) {
    var v = norm(value);
    var t = norm(title) + ' ' + norm(type);
    if (/ AND (SHAM|PILLOWCASE|\d)|SHAMS SET|SET\/2|SET OF 2|BOUDOIR|SQUARE|^DUVET$/.test(v)) return null;
    if (/BEDSKIRT|BED SKIRT/.test(v) || (/BEDSKIRT/.test(t) && sizeOf(v))) {
      var bs = sizeOf(v);
      return bs && bs !== 'fullqueen' ? { piece: 'bedskirt', size: bs } : null;
    }
    if (/SHEET SET|^(TWIN|FULL|QUEEN|KING) SET$/.test(v) || (/SHEET SET|SHEETS?\b/.test(t) && /^(TWIN|FULL|QUEEN|KING)$/.test(v))) {
      var ss = sizeOf(v);
      return ss && ss !== 'fullqueen' ? { piece: 'sheetset', size: ss } : null;
    }
    if (/DUVET/.test(v) || (/DUVET|BEDDING|COLLECTION/.test(t) && /^(TWIN|FULL ?\/ ?QUEEN|FULL QUEEN|KING)$/.test(v))) {
      var ds = sizeOf(v);
      return ds ? { piece: 'duvet', size: ds } : null;
    }
    if (/PILLOWCASE/.test(v) || (/PILLOWCASE/.test(t) && /^(STANDARD|KING)$/.test(v))) {
      return { piece: 'pillowcase', sub: /KING/.test(v) ? 'king' : 'standard' };
    }
    if (/SHAM/.test(v) || /^(GRAND )?EURO$/.test(v) || (/SHAM/.test(t) && /^(STANDARD|KING)$/.test(v))) {
      return { piece: 'sham', sub: subtypeOf(v) };
    }
    return null;
  }

  var COLOR_WORDS = /^[A-Za-zÀ-ſ' ]{2,24}$/;

  function splitTitle(title) {
    var clean = String(title || '')
      .replace(/^\s*NEW( COLOU?RS?)?\s*!?\s*/i, '')
      .replace(/^\s*1\s*UNIT\s*-?\s*/i, '')
      .replace(/^[^A-Za-z]+/, '')
      .trim();
    var m = clean.match(/^(.*?)\s*-\s*([^-]+)$/);
    var base = clean, color = null;
    if (m && COLOR_WORDS.test(m[2].trim()) && m[2].trim().split(' ').length <= 3) {
      base = m[1];
      color = m[2].trim();
    }
    base = base
      .replace(/\b(Duvet|Bedding|Collection|Sets?|Covers?|and|Sham|Shams|Sheets?|Full Queen|Full\/Queen|King|Twin)\b/gi, ' ')
      .replace(/[&,]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return { family: base, color: color };
  }

  function colorOptionIndex(product) {
    for (var i = 0; i < product.options.length; i++) {
      if (/^colou?r$/i.test(product.options[i].trim())) return i;
    }
    return -1;
  }

  function pieceOptionIndex(product) {
    for (var i = 0; i < product.options.length; i++) {
      if (/^(size|style|piece)/i.test(product.options[i].trim())) return i;
    }
    return 0;
  }

  function finishOptionIndex(product) {
    for (var i = 0; i < product.options.length; i++) {
      if (/^finish/i.test(product.options[i].trim())) return i;
    }
    return -1;
  }

  function titleCase(s) {
    return String(s).toLowerCase().replace(/(^|[\s/])([a-zà-ſ])/g, function (m, a, b) { return a + b.toUpperCase(); });
  }

  /*
   * products: [{id, handle, title, type, url, image, options:[name], variants:[{id, price, available, options:[v1,v2,v3]}]}]
   * returns { styles: [{key, name, image, colors:[{name, image, url, pieces:[...]}]}], addons: {sheetset:[], bedskirt:[], pillowcase:[]} }
   */
  function buildModel(products) {
    var styles = {};
    var addons = { sheetset: [], bedskirt: [], pillowcase: [], sham: [] };

    products.forEach(function (p) {
      if (/^1\s*UNIT|\(1\s*UNIT\)|\(1\s*SET\)/i.test(p.title)) return; // single-unit clearance listings (old and new naming)
      var parts = splitTitle(p.title);
      var ci = colorOptionIndex(p);
      var pi = pieceOptionIndex(p);
      var fi = finishOptionIndex(p);
      var byColor = {};

      p.variants.forEach(function (v) {
        var c = classify(v.options[pi], p.title, p.type);
        if (!c) return;
        var color = ci >= 0 ? titleCase(v.options[ci]) : (parts.color ? titleCase(parts.color) : 'As Shown');
        if (fi >= 0 && v.options[fi]) color += ' \u00b7 ' + titleCase(v.options[fi]); // prints: color + finish (Simple Hem / Tatter Ruffle)
        (byColor[color] = byColor[color] || []).push({
          piece: c.piece, size: c.size || null, sub: c.sub || null,
          variantId: v.id, price: v.price, available: v.available, image: v.image || null,
          label: titleCase(v.options[pi])
        });
      });

      Object.keys(byColor).forEach(function (color) {
        var pieces = byColor[color];
        var hasDuvet = pieces.some(function (x) { return x.piece === 'duvet'; });
        if (hasDuvet) {
          var key = parts.family.toLowerCase();
          var s = styles[key] = styles[key] || { key: key, name: p.name || parts.family, detail: p.detail || '', sample: p.title, image: p.image, colors: {} };
          if (!s.detail && p.detail) s.detail = p.detail;
          var existing = s.colors[color];
          if (existing) existing.pieces = existing.pieces.concat(pieces);
          else s.colors[color] = { name: color, image: p.image, url: p.url, pieces: pieces.slice() };
        } else {
          ['sheetset', 'bedskirt', 'pillowcase', 'sham'].forEach(function (kind) {
            var only = pieces.filter(function (x) { return x.piece === kind; });
            if (only.length) addons[kind].push({ productTitle: p.title, name: p.name || parts.family, url: p.url, image: p.image, color: color, family: parts.family, pieces: only });
          });
        }
      });
    });

    var list = Object.keys(styles).map(function (k) {
      var s = styles[k];
      s.colors = Object.keys(s.colors).map(function (c) { return s.colors[c]; })
        .filter(function (c) { return c.pieces.some(function (x) { return x.piece === 'duvet' && x.available; }); });
      return s;
    }).filter(function (s) { return s.colors.length; });

    return { styles: list, addons: addons };
  }

  function duvetSizeFor(bed) {
    return bed === 'full' || bed === 'queen' ? 'fullqueen' : bed;
  }

  // Sham/pillowcase counts that dress a bed of this size by default.
  function defaultCounts(bed) {
    return bed === 'twin' ? { sham: 1, pillowcase: 1 } : { sham: 2, pillowcase: 2 };
  }

  function pieceFor(pieces, piece, bed) {
    var want = piece === 'duvet' ? duvetSizeFor(bed) : bed;
    return pieces.filter(function (x) { return x.piece === piece && x.size === want; })[0] || null;
  }

  var api = { buildModel: buildModel, classify: classify, splitTitle: splitTitle, pieceFor: pieceFor, duvetSizeFor: duvetSizeFor, defaultCounts: defaultCounts, BED_SIZES: BED_SIZES };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.BedBuilderCore = api;
})(this);
